// Last-known recording-consent status, cached per user in localStorage.
//
// This caches SERVER truth so the recording gate can honor the user's real
// consent requirement when the current-user query is unavailable (offline,
// transient API failure). It is a cache of the backend's answer, not a
// client-side simulation of consent state: entries are only ever written
// from a successful current-user resolution, and live data always wins over
// the cache when present.

const CACHE_KEY_PREFIX = "scribenote.recordingConsentStatus.";

export interface CachedConsentStatus {
  requiresRecordingConsent: boolean;
  // Presentation-only modal title; null (e.g. an entry written before the
  // field existed) means callers fall back to the default header.
  consentHeader: string | null;
  consentText: string | null;
  consentTextVersion: string | null;
}

interface ConsentStatusSource {
  requiresRecordingConsent?: boolean | null;
  recordingConsentHeader?: string | null;
  recordingConsentText?: string | null;
  recordingConsentVersion?: string | null;
}

function cacheKey(userUuid: string) {
  return CACHE_KEY_PREFIX + userUuid;
}

/**
 * Persist the consent status carried on a freshly-resolved current user.
 * Called on every successful current-user resolution (see use-current-user).
 * Unusable payloads (no flag, or required without text) are skipped so they
 * never poison a previously usable cache entry.
 */
export function writeCachedConsentStatus(
  userUuid: string | null | undefined,
  user: ConsentStatusSource | null | undefined,
) {
  if (!userUuid || !user || user.requiresRecordingConsent == null) {
    return;
  }
  const requiresRecordingConsent =
    user.requiresRecordingConsent === true;
  if (requiresRecordingConsent && !user.recordingConsentText) {
    return;
  }

  const status: CachedConsentStatus = {
    requiresRecordingConsent,
    consentHeader: user.recordingConsentHeader ?? null,
    consentText: user.recordingConsentText ?? null,
    consentTextVersion: user.recordingConsentVersion ?? null,
  };
  try {
    window.localStorage.setItem(
      cacheKey(userUuid),
      JSON.stringify(status),
    );
  } catch {
    // Storage full or blocked — the cache is best-effort; the gate simply
    // stays fail-closed when live status is unavailable.
  }
}

/**
 * Read the last-known consent status for a user. Returns null when there is
 * no usable entry, in which case callers must fail closed.
 */
export function readCachedConsentStatus(
  userUuid: string | null | undefined,
): CachedConsentStatus | null {
  if (!userUuid) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(userUuid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedConsentStatus;
    if (typeof parsed?.requiresRecordingConsent !== "boolean") {
      return null;
    }
    if (parsed.requiresRecordingConsent && !parsed.consentText) {
      // A "required" status without text cannot render the modal.
      return null;
    }
    return {
      requiresRecordingConsent: parsed.requiresRecordingConsent,
      consentHeader: parsed.consentHeader ?? null,
      consentText: parsed.consentText ?? null,
      consentTextVersion: parsed.consentTextVersion ?? null,
    };
  } catch {
    return null;
  }
}
